import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProviderServices } from '../../../../app/providers/AppServicesContext';
import {
    AgentFaq,
    AgentFile,
    AgentOwnerInfo,
    AgentRelatedLink,
    NexusAgent,
} from '../../../../domain/agents/models/Agent';
import { FunctionToolDefinition } from '../../../../domain/agents/models/FunctionTool';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { acquireNexusApiAccessTokenAsync } from '../../../../services/auth/AuthService';
import { isNexusAgent } from '../../../utils/AgentUtils';
import { alerts } from '../../../../shared/ui/Alerts';
import { FunctionToolFormDefinition, mapFunctionToolsRequestToForm } from '../shared/FunctionToolForm';
import { NexusMcpServerDto } from '../../../../services/providers/nexus/dtos/responses/NexusMcpServerDto';
import { updateAgent } from '../../../../redux/features/agents/agentsSlice';

export interface EditAgentSubmitRequest {
    instructions?: string;
    description?: string;
    agentIconFile?: File;
    model?: string;
    functionTools?: FunctionToolDefinition[];
    filesForFileSearch?: File[];
    filesForCodeInterpreter?: File[];
    enableCodeInterpreter?: boolean;
    enableFileSearch?: boolean;
    mcpServerIds?: string[];
    filesToDeleteForFileSearch?: string[];
    filesToDeleteForCodeInterpreter?: string[];
    metadata?: Record<string, unknown>;
}

export interface EditAgentFormStarterValues {
    description: string;
    instructions: string;
    model: string;
    tools: string[];
    functionTools: FunctionToolFormDefinition[];
    agentIconUrl: string;
    cardShortDescription: string;
    disclaimer: string;
    knowledgeBaseContent: string;
    promptLibrary: { title?: string; prompt: string }[];
    faqs: AgentFaq[];
    ownerInfo: AgentOwnerInfo[];
    supportAddress: string;
    capabilities: string[];
    relatedLinks: AgentRelatedLink[];
    allowFileSearchAtConversationLevel: boolean;
    allowCodeInterpreterAtConversationLevel: boolean;
    selectedMcpServerIds: string[];
}

export interface UseEditAgentPageVm {
    agentId: string | undefined;
    agentName: string | null;
    isLoadingAgent: boolean;
    isAgentMissing: boolean;
    fileSearchDocuments: AgentFile[];
    codeInterpreterDocuments: AgentFile[];
    formStarterValues: EditAgentFormStarterValues | null;
    availableMcpServers: NexusMcpServerDto[];
    isMcpServersLoading: boolean;
    onCancel: () => void;
    onSubmit: (request: EditAgentSubmitRequest) => Promise<void>;
}

const mapDomainToolFlagsToFormTools = (agent: NexusAgent): string[] => {
    const selectedTools: string[] = [];

    if (agent.enableFileSearch) {
        selectedTools.push('fileSearch');
    }

    if (agent.enableCodeInterpreter) {
        selectedTools.push('codeInterpreter');
    }

    if (agent.functionTools && agent.functionTools.length > 0) {
        selectedTools.push('functionCalling');
    }

    if (agent.mcpServerIds && agent.mcpServerIds.length > 0) {
        selectedTools.push('mcpServer');
    }

    return selectedTools;
};

export const useEditAgentPage = (): UseEditAgentPageVm => {
    const navigate = useNavigate();
    const { agentId } = useParams();
    const { instance } = useMsal();
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();

    const agentsById = useAppSelector((state) => state.agents.byId);
    const agentIds = useAppSelector((state) => state.agents.allIds);

    const [availableMcpServers, setAvailableMcpServers] = useState<NexusMcpServerDto[]>([]);
    const [isMcpServersLoading, setIsMcpServersLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetchMcpServers = async () => {
            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({ msalInstance: instance });
                const servers = await providerServices.nexus.mcpServers.getMcpServersAsync(accessToken);
                if (!cancelled) {
                    setAvailableMcpServers(servers);
                }
            } catch {
                // silently fall back to empty list
            } finally {
                if (!cancelled) {
                    setIsMcpServersLoading(false);
                }
            }
        };

        fetchMcpServers();

        return () => {
            cancelled = true;
        };
    }, [instance, providerServices]);

    const selectedAgent = useMemo(() => {
        if (!agentId) {
            return null;
        }

        const agent = agentsById[agentId];
        return isNexusAgent(agent) ? agent : null;
    }, [agentId, agentsById]);

    const formStarterValues = useMemo<EditAgentFormStarterValues | null>(() => {
        if (!selectedAgent) {
            return null;
        }
        return {
            description: selectedAgent.description ?? '',
            instructions: selectedAgent.instructions ?? '',
            model: selectedAgent.model ?? '',
            tools: mapDomainToolFlagsToFormTools(selectedAgent),
            functionTools: mapFunctionToolsRequestToForm(selectedAgent.functionTools),
            agentIconUrl: selectedAgent.agentIconUrl ?? '',
            cardShortDescription: selectedAgent.shortDescription ?? '',
            disclaimer: selectedAgent.disclaimer ?? '',
            knowledgeBaseContent: selectedAgent.knowledgeBase?.content ?? '',
            promptLibrary: selectedAgent.conversationStarters.flatMap((group) =>
                group.items.map((item) => ({
                    title: item.title,
                    prompt: item.prompt,
                })),
            ),
            faqs:
                selectedAgent.faqs && selectedAgent.faqs.length > 0
                    ? selectedAgent.faqs
                    : [{ question: '', answer: '' }],
            ownerInfo:
                selectedAgent.ownerInfo && selectedAgent.ownerInfo.length > 0
                    ? selectedAgent.ownerInfo
                    : [
                          {
                              userId: '',
                              givenName: '',
                              surName: '',
                              userPrincipalName: '',
                              mail: '',
                              photoUrl: '',
                          },
                      ],
            supportAddress: selectedAgent.supportAddress ?? '',
            capabilities:
                selectedAgent.capabilities && selectedAgent.capabilities.length > 0 ? selectedAgent.capabilities : [],
            relatedLinks:
                selectedAgent.relatedLinks && selectedAgent.relatedLinks.length > 0
                    ? selectedAgent.relatedLinks
                    : [{ title: '', link: '' }],
            allowFileSearchAtConversationLevel: selectedAgent.allowFileSearchAtConversationLevel ?? false,
            allowCodeInterpreterAtConversationLevel: selectedAgent.allowCodeInterpreterAtConversationLevel ?? false,
            selectedMcpServerIds: selectedAgent.mcpServerIds ?? [],
        };
    }, [selectedAgent]);

    const isLoadingAgent = !!agentId && !selectedAgent && agentIds.length === 0;
    const isAgentMissing = !!agentId && !selectedAgent && agentIds.length > 0;

    const onCancel = useCallback(() => {
        navigate('/manage-agents');
    }, [navigate]);

    const onSubmit = useCallback(
        async (request: EditAgentSubmitRequest): Promise<void> => {
            if (!agentId) {
                return;
            }

            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const updated = await providerServices.nexus.agents.updateAgentAsync(agentId, request, accessToken);
                dispatch(updateAgent(updated));

                alerts.success('Agent updated successfully.');
                navigate('/manage-agents');
            } catch {
                alerts.error('Failed to update agent. Please try again.');
            }
        },
        [agentId, dispatch, instance, navigate, providerServices],
    );

    return {
        agentId,
        agentName: selectedAgent?.name ?? null,
        isLoadingAgent,
        isAgentMissing,
        fileSearchDocuments: selectedAgent?.fileSearchDocuments ?? [],
        codeInterpreterDocuments: selectedAgent?.codeInterpreterDocuments ?? [],
        formStarterValues,
        availableMcpServers,
        isMcpServersLoading,
        onCancel,
        onSubmit,
    };
};

export default useEditAgentPage;
